<template>
    <main id="liste" v-cloak>
        <HeaderTab :title="$t(page_title)" :swiper_tabs="swiper_tabs">
            <template v-if="!is_payment_screen" v-slot:header-filter>
                <div class="col-12 col-lg-6">
                    <e-select
                        v-model="entity_selected"
                        track-by="tiers_id"
                        label="tiers_rs"
                        :placeholder="$t('tiers.rechercher_billing_entity')"
                        :selectedLabel="$t('global.selected_label')"
                        :options="entity"
                        :searchable="true"
                        :allow-empty="true"
                        :loading="isLoadingEntity"
                        :show-labels="false"
                        :multiple="true"
                        @input="applyEntities"
                    >
                        <template slot="singleLabel" slot-scope="{ option }">{{ option.tiers_rs }}</template>
                        <template slot="noOptions">{{ $t('global.list_empty') }}</template>
                    </e-select>
                </div>
                <div class="col-12 col-lg-6 text-lg-right">
                    <button v-if="has_docoon" class="btn btn-primary ml-2" :disabled="importing" @click="importDocoon">
                        {{ $t('invoice.supplier_import_docoon') }} <font-awesome-icon :icon="['fas', importing === 'docoon' ? 'spinner' : 'download']" :spin="importing === 'docoon'" />
                    </button>
                    <button v-if="has_superpdp" class="btn btn-primary ml-2" :disabled="importing" @click="importSuperPDP">
                        {{ $t('invoice.supplier_import_superpdp') }} <font-awesome-icon :icon="['fas', importing === 'superpdp' ? 'spinner' : 'download']" :spin="importing === 'superpdp'" />
                    </button>
                    <button class="btn btn-secondary ml-2" :title="$t('global.refresh')" @click="refresh">
                        <font-awesome-icon :icon="['fal', 'sync']" :spin="refreshing" />
                    </button>
                </div>
            </template>
        </HeaderTab>

        <div id="content">
            <div class="container-fluid">
                <div class="row">
                    <div class="col-12">
                        <div>
                            <router-view></router-view>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>


<script type="text/javascript">
    import Navigation from "@/mixins/Navigation.js";
    import Accounting from "@/mixins/Accounting.js";
    import Config from "@/mixins/Config.js";
    import Invoice from "@/mixins/Invoice.js";
    import { EventBus } from "EventBus";

    export default {
        name: "Invoice",
        mixins: [Navigation, Accounting, Config, Invoice],
        data () {
            return {
                param_tab: '',
                actual_tab: 'invoiceList',
                entity: [],
                entity_selected: [],
                isLoadingEntity: false,
                counts: {},
                refreshing: false,
                // Comptabilités fournisseurs, conservées pour savoir à quelles plateformes elles sont
                // raccordées.
                accounting_plans: [],
                // Plateforme dont l'import est en cours, pour n'en lancer qu'un à la fois.
                importing: null
            }
        },

        mounted() {
            this.param_tab = this.$route.params.actual_tab
            this.init_component()

            // Une facture qui change de main change aussi de vue : les pastilles se refont après
            // chaque action des listes.
            EventBus.$on('InvoiceSupplier::CountsChanged', this.loadCounts)
        },

        beforeDestroy() {
            EventBus.$off('InvoiceSupplier::CountsChanged', this.loadCounts)
        },

        watch: {
            // Changer d'onglet est le moment naturel pour rafraîchir les compteurs.
            '$route.name': 'loadCounts'
        },

        computed: {
            /**
             * Les règlements partagent cet écran par leur adresse, mais n'appartiennent plus au suivi
             * des factures : ils s'atteignent par le menu et n'ont pas à en porter les onglets.
             */
            is_payment_screen() {
                return this.$route.name === 'invoiceSupplierPayment'
            },

            /**
             * Les factures à ventiler partagent elles aussi l'écran par leur adresse, mais recoupent
             * toutes les vues de suivi au lieu d'en être une : elles n'en portent pas les onglets. Le
             * filtre de comptabilité leur reste utile — c'est le plan comptable de l'entité qui fournit
             * les comptes d'imputation.
             */
            is_to_allocate_screen() {
                return this.$route.name === 'invoiceSupplierToAllocate'
            },

            /**
             * Titre de l'écran : les règlements et les factures à ventiler ont le leur, le reste relève
             * du suivi des factures fournisseurs.
             */
            page_title() {
                if(this.is_payment_screen) {
                    return 'invoice.supplier_payments'
                }

                if(this.is_to_allocate_screen) {
                    return 'invoice.supplier_to_allocate'
                }

                return 'compta.facturation_fournisseur'
            },

            /**
             * Onglets de suivi, dans l'ordre du traitement d'une facture : de son arrivée à son
             * règlement, les impasses — litige, suspension, rejet — venant après le fil principal.
             */
            swiper_tabs() {
                if(this.is_payment_screen || this.is_to_allocate_screen) {
                    return []
                }

                const buckets = [
                    ['inbox', 'invoiceSupplierControl'],
                    ['to-process', 'invoiceSupplierToProcess'],
                    ['approved', 'invoiceSupplierApproved'],
                    ['partially-approved', 'invoiceSupplierPartiallyApproved'],
                    ['disputed', 'invoiceSupplierDisputed'],
                    ['suspended', 'invoiceSupplierSuspended'],
                    ['rejected', 'invoiceSupplierRejected'],
                    ['partially-paid', 'invoiceSupplierPartiallyPaid'],
                    ['paid', 'invoiceSupplierPaid'],
                ]

                return buckets.map(([bucket, href]) => ({
                    id: bucket,
                    label: 'invoice.supplier_bucket.' + bucket,
                    active: false,
                    href,
                    // Ce qui reste à traiter se compte ; les affaires closes, non — leur pastille ne
                    // ferait que grossir sans rien dire de l'ouvrage en cours. Une vue vide n'affiche
                    // pas de zéro : l'absence de pastille le dit déjà.
                    badge: this.counts[bucket] || undefined
                }))
            },

            /**
             * Les imports de plateforme ne sont proposés que si une comptabilité y est raccordée :
             * ailleurs, le bouton ne pourrait que rapporter une liste vide.
             */
            has_docoon() {
                return this.accounting_plans.some(plan => plan.accountingplan_docoon_connected)
            },

            has_superpdp() {
                return this.accounting_plans.some(plan => plan.accountingplan_superpdp_connected)
            },

            hasSaillieAccess() {
                return (this.$store.state.userAccess.hasSaillieAccess)
            },
        },

        methods: {
            /**
             * Seules les comptabilités déclarées fournisseurs alimentent le filtre : les autres n'ont
             * pas de factures reçues. La sélection précédente est reprise, elle vaut pour la page.
             */
            async init_component() {
                this.isLoadingEntity = true

                const accounting = await this.loadAccountingPlans()
                this.accounting_plans = (accounting || []).filter(acc => acc.accountingplan_supplier)
                this.entity = this.accounting_plans.map(acc => acc.tiers)

                // La sélection précédente est reprise : elle vaut pour toute la page, et l'on ne
                // redemande pas à chaque visite ce qui a déjà été choisi. Mémorisée dans le navigateur
                // et non par licence, elle peut désigner les comptabilités d'une autre licence : aucune
                // ne correspondant, on repart de toutes, comme lors d'une première visite.
                const previous = this.getConfig('invoice_suppliers_entities') || []
                const kept = this.entity.filter(en => previous.includes(en.tiers_id))
                this.entity_selected = kept.length > 0 ? kept : this.entity.slice()

                this.isLoadingEntity = false

                // Les listes se sont chargées au montage avec la sélection mémorisée telle quelle : si
                // elle a été corrigée, elle est réenregistrée et les listes rechargées, faute de quoi
                // elles filtreraient sur des comptabilités que l'en-tête n'affiche plus.
                const selected_ids = this.entity_selected.map(tiers => tiers.tiers_id)
                if(selected_ids.length !== previous.length || selected_ids.some(id => !previous.includes(id))) {
                    this.applyEntities()
                }
                else {
                    this.loadCounts()
                }
            },

            /**
             * Le filtre vaut pour tous les onglets : il est mémorisé puis annoncé aux listes, qui se
             * rechargent. Les compteurs suivent le même mouvement.
             */
            applyEntities() {
                this.setConfig('invoice_suppliers_entities', this.entity_selected.map(tiers => tiers.tiers_id))

                EventBus.$emit('InvoiceSupplier::EntitiesChanged')

                this.loadCounts()
            },

            async loadCounts() {
                this.counts = await this.getInvoiceSupplierCounts(this.entity_selected.map(tiers => tiers.tiers_id))
            },

            /**
             * Redemande la vue courante et ses compteurs. Le dépôt d'une facture lance un OCR en
             * tâche de fond : ses données n'existent pas encore quand la liste s'affiche.
             */
            async refresh() {
                this.refreshing = true

                EventBus.$emit('InvoiceSupplier::Refresh')
                await this.loadCounts()

                this.refreshing = false
            },

            importDocoon() {
                return this.importFrom('docoon', this.importInvoicesSuppliersDocoon)
            },

            importSuperPDP() {
                return this.importFrom('superpdp', this.importInvoicesSuppliersSuperPDP)
            },

            /**
             * L'import porte sur les entités sélectionnées dans le bandeau, et les factures reçues
             * arrivent en boîte de réception : la vue courante et les pastilles se refont.
             */
            async importFrom(platform, request) {
                this.importing = platform

                await request(this.entity_selected.map(tiers => tiers.tiers_id))

                this.importing = null
                this.successToast('toast.info_save_succes')

                EventBus.$emit('InvoiceSupplier::Refresh')
                this.loadCounts()
            }
        },
        components: {
            HeaderTab: () => import('@/components/HeaderTab'),
        }
    }
</script>
